package com.dotdrives.web.pages.givers

import com.dotdrives.controller.GiverExperienceController
import com.dotdrives.controller.GiverExperienceUiState
import com.dotdrives.web.pages.dashboard.money
import com.dotdrives.web.ui.useDialogFocus
import com.dotdrives.controller.GiverDetailController
import com.dotdrives.controller.GiverDetailUiState
import com.dotdrives.design.DotAvatarPalette
import com.dotdrives.model.GiverSummary
import com.dotdrives.web.ui.DotBanner
import com.dotdrives.web.ui.DotSkeleton
import kotlinx.browser.document
import react.FC
import react.Key
import react.Props
import react.dom.aria.AriaRole
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.span
import react.useState
import web.cssom.ClassName
import web.html.ButtonType
import web.html.button as buttonType

external interface GiverDrawerProps : Props {
    var userId: String
    var orgId: String
    var experienceController: GiverExperienceController
    var experienceState: GiverExperienceUiState
    var modalBlocked: Boolean
    var canFinance: Boolean
    var onReminder: () -> Unit
    var summary: GiverSummary
    var state: GiverDetailUiState
    var controller: GiverDetailController
    var canWrite: Boolean
    var onEdit: () -> Unit
    var onClose: () -> Unit
}

val GiverDrawer = FC<GiverDrawerProps> { props ->
    val (tab, setTab) = useState("activity")
    useDialogFocus(true, "giver-detail-dialog", active = !props.modalBlocked, onClose = props.onClose)
    val giver = props.state.giver
    val (avatarBackground, avatarText) = DotAvatarPalette.tintFor(props.summary.id)
    val avatarStyle = js("({})")
    avatarStyle.backgroundColor = avatarBackground
    avatarStyle.color = com.dotdrives.web.ui.accessibleAvatarText(avatarText)

    div {
        className = ClassName("givers-backdrop")
        role = "presentation".unsafeCast<AriaRole>()
        onClick = { props.onClose() }

        div {
            id = web.dom.ElementId("giver-detail-dialog")
            className = ClassName("givers-drawer")
            role = "dialog".unsafeCast<AriaRole>()
            ariaModal = true
            ariaLabel = "Giver details"
            onClick = { it.stopPropagation() }

            if (props.state.loading && giver == null) {
                div {
                    className = ClassName("givers-drawer-loading")
                    repeat(6) { DotSkeleton { height = if (it == 0) "54px" else "42px" } }
                }
            } else {
                div {
                    className = ClassName("givers-drawer-head")
                    div {
                        className = ClassName("givers-drawer-person")
                        div {
                            className = ClassName("givers-avatar givers-drawer-avatar")
                            style = avatarStyle
                            +giverInitials(giver?.displayName ?: props.summary.displayName)
                        }
                        div {
                            className = ClassName("givers-person-copy")
                            div {
                                className = ClassName("givers-drawer-name")
                                +(giver?.displayName ?: props.summary.displayName)
                                if (giver?.isActive == false || !props.summary.isActive) +" · Inactive"
                            }
                            div {
                                className = ClassName("givers-drawer-meta")
                                val type = giver?.giverType ?: props.summary.giverType
                                +"${type.replaceFirstChar { it.uppercase() }} · Steward: ${props.summary.stewardName ?: "Unassigned"}"
                            }
                        }
                        button {
                            type = ButtonType.buttonType
                            className = ClassName("givers-drawer-close")
                            ariaLabel = "Close giver details"
                            onClick = { props.onClose() }
                            +"✕"
                        }
                    }
                    div {
                        className = ClassName("givers-drawer-actions")
                        if (props.canWrite && (giver?.isActive ?: props.summary.isActive)) button {
                            type = ButtonType.buttonType
                            className = ClassName("givers-action-pill")
                            onClick = { props.onReminder() }
                            +"+ Reminder"
                        }
                        if (props.canWrite && (giver?.isActive ?: props.summary.isActive)) {
                            button {
                                type = ButtonType.buttonType
                                className = ClassName("givers-action-pill")
                                onClick = {
                                    setTab("notes")
                                    kotlinx.browser.window.setTimeout({ document.getElementById("giver-note-body")?.asDynamic()?.focus() }, 0)
                                }
                                +"+ Note"
                            }
                            button {
                                type = ButtonType.buttonType
                                className = ClassName("givers-action-pill")
                                onClick = { props.onEdit() }
                                +"✎ Edit"
                            }
                        }
                    }
                    div {
                        className = ClassName("givers-stats")
                        val stats = props.experienceState.stats
                        val values = if (props.canFinance) listOf(
                            "LIFETIME" to (stats?.finance?.lifetime?.let(::money) ?: "—"),
                            "THIS YEAR" to (stats?.finance?.yearTotal?.let(::money) ?: "—"),
                            "DRIVES" to (stats?.driveCount?.toString() ?: "—"),
                        ) else listOf("DRIVES" to (stats?.driveCount?.toString() ?: "—"), "ACTIVE" to (stats?.activeDriveCount?.toString() ?: "—"))
                        values.forEach { (label, value) ->
                            div { className = ClassName("givers-stat")
                                div { className = ClassName("givers-stat-label"); +label }
                                div { className = ClassName("givers-stat-value"); +value }
                            }
                        }

                    }
                }

                div {
                    className = ClassName("givers-drawer-tabs")
                    (listOf("activity" to "Activity", "notes" to "Notes", "files" to "Files") + if (props.canFinance) listOf("gifts" to "Gifts", "recurring" to "Recurring", "tax" to "Tax") else emptyList()).forEach { (value, label) ->
                        button {
                            type = ButtonType.buttonType
                            className = ClassName(
                                "givers-tab" + if (tab == value) " givers-tab-active" else "",
                            )
                            onClick = {
                                setTab(value)
                                if (value == "notes") {
                                    props.controller.showNotesTimeline()
                                } else if (value == "activity") {
                                    props.controller.showAllTimeline()
                                }
                            }
                            +label
                        }
                    }
                }

                div {
                    className = ClassName("givers-drawer-body")
                    props.state.error?.let { error ->
                        DotBanner {
                            message = error
                            onRetry = { props.controller.open(props.summary.id) }
                            className = "givers-inline-banner"
                        }
                    }
                    props.experienceState.error?.let { error -> DotBanner { message = error; onRetry = { props.experienceController.open(props.summary.id) } } }
                    if (tab == "recurring" && props.canFinance) {
                        com.dotdrives.web.pages.donations.RecurringSchedulesPanel {
                            userId = props.userId
                            orgId = props.orgId
                            giverId = props.summary.id
                        }
                    } else if (tab in listOf("gifts", "tax", "files")) {
                        GiverExperienceTabs { this.tab = tab; state = props.experienceState; controller = props.experienceController; canWrite = props.canWrite }
                    } else if (tab == "activity") {
                        div {
                            className = ClassName("givers-timeline-filters")
                            listOf(
                                Triple("All", props.state.showingAllTimeline, props.controller::showAllTimeline),
                                Triple("Notes", props.state.showingNotesTimeline, props.controller::showNotesTimeline),
                            ).forEach { (label, selected, select) ->
                                button {
                                    type = ButtonType.buttonType
                                    className = ClassName(
                                        "givers-filter" +
                                            if (selected) " givers-filter-active" else "",
                                    )
                                    onClick = { select() }
                                    +label
                                }
                            }
                        }
                        if (props.state.loading) {
                            div {
                                className = ClassName("givers-drawer-loading")
                                repeat(4) { DotSkeleton { height = "74px" } }
                            }
                        } else {
                            TimelineList {
                                items = props.state.timeline
                            }
                        }
                    } else if (props.state.notes.isEmpty()) {
                        div {
                            className = ClassName("givers-empty-copy")
                            +"No notes yet."
                        }
                    } else {
                        props.state.notes.forEach { note ->
                            div {
                                key = note.id.unsafeCast<Key>()
                                className = ClassName("givers-note-card")
                                div {
                                    className = ClassName("givers-note-title-row")
                                    div {
                                        className = ClassName("givers-note-title")
                                        +note.title.ifBlank { "Note" }
                                    }
                                    div {
                                        className = ClassName("givers-timeline-date")
                                        +formatTimelineDate(note.createdAt.toString())
                                    }
                                }
                                div {
                                    className = ClassName("givers-note-body")
                                    +note.body
                                }
                            }
                        }
                    }
                    if (props.canWrite && tab in listOf("notes", "activity")) {
                        NoteComposer {
                            controller = props.controller
                            state = props.state
                        }
                    }
                }
            }
        }
    }
}
